/* iter155 — App mode helper.
 *
 * Users can toggle between two "modes" of the home screen:
 *   • "discover" (default) — the existing Discover home content.
 *   • "ride"              — the Ride Mode ambient screen with Habana
 *                            silhouette, weather, exchange rates, and
 *                            two big Ride/History buttons.
 *
 * Persisted to localStorage `habano.mode`. A window event fires on
 * change so any component (Home, ProfileDropdown) can subscribe and
 * re-render immediately without full page reload.
 */
import { useEffect, useState } from "react";
import { lsGet, lsSet } from "./safeStorage";
import { isDiscoverAvailableSync } from "./discoverAvailability";

const KEY = "habano.mode";
const EVT = "habano:mode-changed";

export function getMode() {
  // iter170 — When the global Discover switch is OFF (and the user is
  // NOT an admin) the app opens directly into Ride mode regardless of
  // any previously stored preference.
  if (!isDiscoverAvailableSync()) return "ride";
  // iter165 — Default launch mode is Ride when the user has no stored
  // preference (fresh install / never toggled). Once the user picks
  // Discover from the intro or via the profile-menu toggle the choice
  // is persisted and honoured on every subsequent launch.
  const v = (lsGet(KEY) || "").toLowerCase();
  if (v === "discover") return "discover";
  return "ride";
}

export function setMode(next) {
  const norm = next === "ride" ? "ride" : "discover";
  // iter170 — Silently coerce to "ride" when Discover is globally
  // disabled so components that call setMode('discover') without
  // gating still land safely.
  const guarded = norm === "discover" && !isDiscoverAvailableSync() ? "ride" : norm;
  try { lsSet(KEY, guarded); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent(EVT, { detail: guarded })); } catch { /* ignore */ }
  return guarded;
}

export function useMode() {
  const [mode, setModeState] = useState(() => getMode());
  useEffect(() => {
    const onEvt = (e) => setModeState(e?.detail || getMode());
    const onStorage = (e) => { if (e.key === KEY) setModeState(getMode()); };
    window.addEventListener(EVT, onEvt);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVT, onEvt);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  const toggle = () => setMode(mode === "ride" ? "discover" : "ride");
  return [mode, setMode, toggle];
}
